import React, { useState, useCallback, useContext } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Table,
  Progress,
  Alert,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
} from 'reactstrap';
import { ContentSection } from '../../components/Layout/ContentSection';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { LayoutStoreInstance } from '@/LayoutStore';
import { Routes } from '@/utils/routes';
import { Link } from 'react-router-dom';
import { ProcessAccountLedgersDocument } from '@/graphql/mutations/processAccountLedgers.generated';
import { GetLastAccountLedgerProcessedConfigDocument } from '@/graphql/queries/getLastAccountLedgerProcessedConfig.generated';
import { SetLastAccountLedgerProcessedConfigDocument } from '@/graphql/mutations/setLastAccountLedgerProcessedConfig.generated';
import {
  ViewerContext,
  IsFinance,
  IsAdmin,
} from '@/providers/ViewerContextProvider';
import type {
  AccountLedgerInput,
  ProcessAccountLedgerResult,
  ProcessAccountLedgersResult,
} from '@/graphql/generatedTypes';

type ParsedCsvRow = {
  date: string;
  serviceDate: string;
  businessUnit: string;
  debitAccount: string;
  contraAccount: string;
  postingText: string;
  taxRate: string;
  documentField1: string;
  debitAmount: string;
  creditAmount: string;
  currency: string;
  inputAmount: string;
  exchangeRate: string;
  batchNumber: string;
  postingNumber: string;
  handelskontenrahmen: string;
  documentField2: string;
  costCenter1: string;
  costCenter2: string;
  costQuantity: string;
  paymentGoal: string;
};

const CHUNK_SIZE = 50;

// List of debitor account numbers to ignore (will be filtered out)
const IGNORED_DEBITOR_ACCOUNT_NUMBERS = [
  '137000',
  '126505',
  '372000',
  '373000',
  '374000',
];

export default function AccountLedgerCsvUpload() {
  const viewer = useContext(ViewerContext);

  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<ParsedCsvRow[]>([]);
  const [showAllRows, setShowAllRows] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState<ProcessAccountLedgerResult[]>([]);
  const [processedIndices, setProcessedIndices] = useState<number[]>([]); // Track which indices were processed
  const [summary, setSummary] = useState<Omit<
    ProcessAccountLedgersResult,
    'results'
  > | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorDetails, setErrorDetails] = useState<string>('');

  const { data: lastProcessedData, refetch: refetchLastProcessed } = useQuery(
    GetLastAccountLedgerProcessedConfigDocument,
  );

  const [processAccountLedgers] = useMutation(ProcessAccountLedgersDocument);
  const [setLastAccountLedgerProcessedConfig] = useMutation(
    SetLastAccountLedgerProcessedConfigDocument,
  );

  const canSubmit = IsFinance(viewer) || IsAdmin(viewer);

  // Helper function to parse CSV line with support for quoted fields
  const parseCsvLine = (line: string, delimiter: string = ';'): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        // Toggle quote mode
        inQuotes = !inQuotes;
      } else if (char === delimiter && !inQuotes) {
        // Found delimiter outside of quotes, push current field
        result.push(current);
        current = '';
      } else {
        // Add character to current field
        current += char;
      }
    }

    // Push the last field
    result.push(current);

    return result;
  };

  // Helper function to validate German date format (DD.MM.YYYY)
  const isValidGermanDate = (dateString: string): boolean => {
    if (!dateString) return false;

    // Check format: DD.MM.YYYY
    const dateRegex = /^(\d{2})\.(\d{2})\.(\d{4})$/;
    const match = dateString.match(dateRegex);

    if (!match) return false;

    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);

    // Validate ranges
    if (month < 1 || month > 12) return false;
    if (day < 1 || day > 31) return false;
    if (year < 1900 || year > 2100) return false;

    // Check if date is valid (e.g., not 31.02.2025)
    const date = new Date(year, month - 1, day);
    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  };

  // Helper function to check if a row should be ignored
  const isRowIgnored = (row: ParsedCsvRow): boolean => {
    const accountNumber = row.contraAccount.replace(/\s/g, '');
    return IGNORED_DEBITOR_ACCOUNT_NUMBERS.includes(accountNumber);
  };

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];
      if (!selectedFile) return;

      setFile(selectedFile);
      setError(null);
      setParsedData([]);
      setResults([]);
      setSummary(null);

      // Read and parse the CSV file
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter((line) => line.trim());

          if (lines.length === 0) {
            setError(i18n.t('AccountLedger:emptyFile'));
            return;
          }

          // Detect CSV format and determine how many lines to skip
          const firstLine = lines[0];
          let dataLines: string[];

          if (firstLine.startsWith('Kanzlei-Rechnungswesen')) {
            // Format 1: Starts with "Kanzlei-Rechnungswesen"
            // Skip first line (header description) and second line (column headers)
            dataLines = lines.slice(2);
          } else if (firstLine.startsWith('Datum;')) {
            // Format 2: Starts directly with column headers "Datum;Leistungsdatum;..."
            // Skip only the first line (column headers)
            dataLines = lines.slice(1);
          } else {
            // Unknown format
            setError(i18n.t('AccountLedger:invalidFileFormat'));
            return;
          }

          // Calculate how many lines were skipped for error reporting
          const linesSkipped = firstLine.startsWith('Kanzlei-Rechnungswesen')
            ? 2
            : 1;

          const parsed = dataLines.map((line, lineIndex) => {
            const values = parseCsvLine(line, ';');
            return {
              date: values[0] || '',
              serviceDate: values[1] || '',
              businessUnit: values[2] || '',
              debitAccount: values[3] || '',
              contraAccount: values[4] || '',
              postingText: values[5] || '',
              taxRate: values[6] || '',
              documentField1: values[7] || '',
              debitAmount: values[8] || '',
              creditAmount: values[9] || '',
              currency: values[10] || '',
              inputAmount: values[11] || '',
              exchangeRate: values[12] || '',
              batchNumber: values[13] || '',
              postingNumber: values[14] || '',
              handelskontenrahmen: values[15] || '',
              documentField2: values[16] || '',
              costCenter1: values[17] || '',
              costCenter2: values[18] || '',
              costQuantity: values[19] || '',
              paymentGoal: values[20] || '',
            };
          });

          // Validate all dates are in German format
          const invalidDates: { row: number; date: string }[] = [];
          parsed.forEach((row, index) => {
            if (row.date && !isValidGermanDate(row.date)) {
              invalidDates.push({
                row: index + linesSkipped + 1,
                date: row.date,
              }); // +1 for 1-based line numbers
            }
          });

          if (invalidDates.length > 0) {
            const errorMsg =
              invalidDates.length === 1
                ? i18n.t('AccountLedger:invalidDateFormat', {
                    row: invalidDates[0].row,
                    date: invalidDates[0].date,
                  })
                : i18n.t('AccountLedger:invalidDateFormatMultiple', {
                    count: invalidDates.length,
                    firstRow: invalidDates[0].row,
                    firstDate: invalidDates[0].date,
                  });
            setError(errorMsg);
            setParsedData([]);
            return;
          }

          setParsedData(parsed);
          LayoutStoreInstance.setSuccess(
            i18n.t('AccountLedger:fileParsedSuccessfully', {
              count: parsed.length,
            }),
          );
        } catch (err) {
          setError(
            i18n.t('AccountLedger:parseError', {
              error: err instanceof Error ? err.message : String(err),
            }),
          );
        }
      };

      reader.onerror = () => {
        setError(i18n.t('AccountLedger:fileReadError'));
      };

      reader.readAsText(selectedFile, 'UTF-8');
    },
    [],
  );

  const convertToAccountLedgerInput = (
    row: ParsedCsvRow,
  ): AccountLedgerInput => {
    // Parse German date format (DD.MM.YYYY) to ISO format (YYYY-MM-DD)
    const parseGermanDate = (dateString: string): string | undefined => {
      if (!dateString) return undefined;
      const parts = dateString.split('.');
      if (parts.length !== 3) return undefined;
      const [day, month, year] = parts;
      // Return ISO format: YYYY-MM-DD
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    };

    // Parse amount as string for Decimal type (remove currency symbols, thousands separator, replace comma with dot)
    const parseDecimalAmount = (value: string): string | undefined => {
      if (!value) return undefined;
      // Remove currency symbols (€, $, etc.), trim whitespace, remove thousands separators, replace decimal comma with dot
      const cleaned = value
        .trim()
        .replace(/[€$£¥]/g, '') // Remove common currency symbols
        .trim() // Trim again after removing currency symbols
        .replace(/\./g, '') // Remove thousands separator (German format uses dots)
        .replace(',', '.'); // Replace decimal comma with dot
      return cleaned;
    };

    const parseInt = (value: string): number | undefined => {
      if (!value) return undefined;
      const parsed = Number(value);
      return isNaN(parsed) ? undefined : parsed;
    };

    return {
      posting_number: parseInt(row.postingNumber) || 0,
      booking_date: parseGermanDate(row.date), // Datum in ISO format
      debit_amount: parseDecimalAmount(row.debitAmount), // String for Decimal type
      credit_amount: parseDecimalAmount(row.creditAmount), // String for Decimal type
      debitor_account_number: row.contraAccount
        ? row.contraAccount.replace(/\s/g, '')
        : undefined, // Gegenkonto (remove all spaces)
      batch_number: row.batchNumber || undefined,
      cost_center_1: row.costCenter1 || undefined,
      cost_center_2: row.costCenter2 || undefined,
      statement_descriptor: row.postingText || undefined, // Buchungstext
      invoice_reference: row.documentField1 || undefined, // Belegfeld1
    };
  };

  const handleSubmit = async () => {
    if (parsedData.length === 0) {
      setError(i18n.t('AccountLedger:noDataToProcess'));
      return;
    }

    // Check permissions
    if (!canSubmit) {
      setError(i18n.t('AccountLedger:insufficientPermissions'));
      return;
    }

    // Filter out ignored rows and track their original indices
    const filteredDataWithIndices = parsedData
      .map((row, index) => ({ row, originalIndex: index }))
      .filter(({ row }) => !isRowIgnored(row));

    if (filteredDataWithIndices.length === 0) {
      setError(i18n.t('AccountLedger:allRowsIgnored'));
      return;
    }

    const filteredData = filteredDataWithIndices.map((item) => item.row);
    const indices = filteredDataWithIndices.map((item) => item.originalIndex);

    setIsProcessing(true);
    setProgress({ current: 0, total: filteredData.length });
    setResults([]);
    setProcessedIndices(indices);
    setSummary(null);
    setError(null);

    try {
      // Convert parsed data to AccountLedgerInput (only non-ignored rows)
      const ledgerInputs = filteredData.map(convertToAccountLedgerInput);

      // Split into chunks
      const chunks: AccountLedgerInput[][] = [];
      for (let i = 0; i < ledgerInputs.length; i += CHUNK_SIZE) {
        chunks.push(ledgerInputs.slice(i, i + CHUNK_SIZE));
      }

      let allResults: ProcessAccountLedgerResult[] = [];
      let totalCreated = 0;
      let totalUpdated = 0;
      let totalProcessed = 0;
      let totalInvoiceLinked = 0;

      // Process chunks sequentially
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];

        const response = await processAccountLedgers({
          variables: {
            account_ledgers: chunk,
          },
        });

        // Check for errors in the response
        if (response.errors && response.errors.length > 0) {
          const errorMessage = response.errors
            .map((err) => err.message)
            .join('\n');
          const errorMsg = i18n.t('AccountLedger:mutationError');
          setError(errorMsg);
          setErrorDetails(errorMessage);
          setErrorModalOpen(true);
          setIsProcessing(false);
          return;
        }

        if (response.data?.processAccountLedgers) {
          const result = response.data.processAccountLedgers;
          allResults = [...allResults, ...result.results];
          totalCreated += result.created_count;
          totalUpdated += result.updated_count;
          totalProcessed += result.processed_count;
          totalInvoiceLinked += result.invoice_linked_count;

          setResults(allResults);
          setProgress({
            current: (i + 1) * CHUNK_SIZE,
            total: parsedData.length,
          });
        }
      }

      setSummary({
        created_count: totalCreated,
        updated_count: totalUpdated,
        processed_count: totalProcessed,
        invoice_linked_count: totalInvoiceLinked,
        __typename: 'ProcessAccountLedgersResult',
      });

      // Update last processed config with current timestamp and first batch number
      const firstBatchNumber = parsedData[0]?.batchNumber;
      if (firstBatchNumber) {
        try {
          await setLastAccountLedgerProcessedConfig({
            variables: {
              input: {
                last_account_ledger_batch_number: firstBatchNumber,
                last_account_ledger_processed_at: new Date().toISOString(),
              },
            },
          });
          // Refetch to update the display
          await refetchLastProcessed();
        } catch (configError) {
          console.error('Failed to update last processed config:', configError);
          // Don't fail the entire operation if this fails
        }
      }

      LayoutStoreInstance.setSuccess(
        i18n.t('AccountLedger:processingCompleted'),
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);
      setError(
        i18n.t('AccountLedger:processingError', {
          error: errorMessage,
        }),
      );
      setErrorDetails(errorMessage);
      setErrorModalOpen(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const progressPercentage =
    progress.total > 0 ? (progress.current / progress.total) * 100 : 0;

  return (
    <ContentSection
      title={i18n.t('AccountLedger:pageTitle')}
      collapsable={false}
    >
      {/* Last Processed Info */}
      {lastProcessedData?.getLastAccountLedgerProcessedConfig && (
        <Card className="mb-4">
          <CardBody>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <h6 className="mb-2">
                  {i18n.t('AccountLedger:lastProcessedInfo')}
                </h6>
                <p className="mb-1">
                  <strong>{i18n.t('AccountLedger:lastBatchNumber')}:</strong>{' '}
                  <Badge color="info">
                    {
                      lastProcessedData.getLastAccountLedgerProcessedConfig
                        .last_account_ledger_batch_number
                    }
                  </Badge>
                </p>
                <p className="mb-0">
                  <strong>{i18n.t('AccountLedger:lastProcessedAt')}:</strong>{' '}
                  {new Date(
                    lastProcessedData.getLastAccountLedgerProcessedConfig.last_account_ledger_processed_at,
                  ).toLocaleDateString(
                    i18n.language,
                    getDateFormatOptions(true),
                  )}
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* File Upload Section */}
      <Card className="mb-4">
        <CardHeader>
          <h5>{i18n.t('AccountLedger:uploadCsvFile')}</h5>
        </CardHeader>
        <CardBody>
          <div className="mb-3">
            <Input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              disabled={isProcessing}
            />
            <small className="form-text text-muted">
              {i18n.t('AccountLedger:uploadHelp')}
            </small>
          </div>

          {error && (
            <Alert color="danger" className="mb-3">
              {error}
            </Alert>
          )}

          {file && parsedData.length > 0 && (
            <div>
              <p>
                <strong>{i18n.t('AccountLedger:fileName')}:</strong> {file.name}
              </p>
              <p>
                <strong>{i18n.t('AccountLedger:rowsCount')}:</strong>{' '}
                {parsedData.length}
              </p>
              {parsedData.filter((row) => isRowIgnored(row)).length > 0 && (
                <p className="text-warning">
                  <strong>{i18n.t('AccountLedger:ignoredRowsCount')}:</strong>{' '}
                  {parsedData.filter((row) => isRowIgnored(row)).length} (
                  {i18n.t('AccountLedger:willNotBeProcessed')})
                </p>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Data Preview Section */}
      {parsedData.length > 0 && (
        <Card className="mb-4">
          <CardHeader>
            <h5>{i18n.t('AccountLedger:dataPreview')}</h5>
          </CardHeader>
          <CardBody>
            <div style={{ overflowX: 'auto', maxHeight: '400px' }}>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>{i18n.t('AccountLedger:date')}</th>
                    <th>{i18n.t('AccountLedger:postingNumber')}</th>
                    <th>{i18n.t('AccountLedger:debitorAccountNumber')}</th>
                    <th>{i18n.t('AccountLedger:statementDescriptor')}</th>
                    <th>{i18n.t('AccountLedger:invoiceReference')}</th>
                    <th>{i18n.t('AccountLedger:debitAmount')}</th>
                    <th>{i18n.t('AccountLedger:creditAmount')}</th>
                    <th>{i18n.t('AccountLedger:batchNumber')}</th>
                    <th>{i18n.t('AccountLedger:costCenter1')}</th>
                    <th>{i18n.t('AccountLedger:costCenter2')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllRows ? parsedData : parsedData.slice(0, 50)).map(
                    (row, index) => {
                      const ignored = isRowIgnored(row);
                      const rowStyle = ignored
                        ? { textDecoration: 'line-through', opacity: 0.6 }
                        : {};
                      return (
                        <tr key={index} style={rowStyle}>
                          <td>{row.date}</td>
                          <td>{row.postingNumber}</td>
                          <td>{row.contraAccount}</td>
                          <td>{row.postingText}</td>
                          <td>{row.documentField1}</td>
                          <td>{row.debitAmount}</td>
                          <td>{row.creditAmount}</td>
                          <td>{row.batchNumber}</td>
                          <td>{row.costCenter1}</td>
                          <td>{row.costCenter2}</td>
                        </tr>
                      );
                    },
                  )}
                </tbody>
              </Table>
              {parsedData.length > 50 && !showAllRows && (
                <p className="text-muted">
                  {i18n.t('AccountLedger:showingFirstRows', { count: 50 })}{' '}
                  <button
                    type="button"
                    onClick={() => setShowAllRows(true)}
                    className="btn btn-link text-primary p-0 align-baseline"
                    style={{ textDecoration: 'underline' }}
                  >
                    {i18n.t('AccountLedger:showAll')}
                  </button>
                </p>
              )}
              {parsedData.length > 50 && showAllRows && (
                <p className="text-muted">
                  {i18n.t('AccountLedger:showingAllRows', {
                    count: parsedData.length,
                  })}{' '}
                  <button
                    type="button"
                    onClick={() => setShowAllRows(false)}
                    className="btn btn-link text-primary p-0 align-baseline"
                    style={{ textDecoration: 'underline' }}
                  >
                    {i18n.t('AccountLedger:showLess')}
                  </button>
                </p>
              )}
            </div>

            <Button
              color="primary"
              onClick={handleSubmit}
              disabled={isProcessing || !canSubmit}
              className="mt-3"
              title={
                !canSubmit
                  ? i18n.t('AccountLedger:insufficientPermissions')
                  : undefined
              }
            >
              {isProcessing
                ? i18n.t('AccountLedger:processing')
                : i18n.t('AccountLedger:submitData')}
            </Button>
            {!canSubmit && (
              <p className="text-danger mt-2 mb-0">
                <small>{i18n.t('AccountLedger:insufficientPermissions')}</small>
              </p>
            )}
          </CardBody>
        </Card>
      )}

      {/* Progress Section */}
      {isProcessing && (
        <Card className="mb-4">
          <CardHeader>
            <h5>{i18n.t('AccountLedger:processingProgress')}</h5>
          </CardHeader>
          <CardBody>
            <Progress
              value={progressPercentage}
              className="mb-2"
              style={{ height: '25px' }}
            >
              {Math.round(progressPercentage)}%
            </Progress>
            <p>
              {i18n.t('AccountLedger:progressText', {
                current: Math.min(progress.current, progress.total),
                total: progress.total,
              })}
            </p>
          </CardBody>
        </Card>
      )}

      {/* Summary Section */}
      {summary && (
        <Card className="mb-4">
          <CardHeader>
            <h5>{i18n.t('AccountLedger:summary')}</h5>
          </CardHeader>
          <CardBody>
            <Table>
              <tbody>
                <tr>
                  <td>
                    <strong>{i18n.t('AccountLedger:totalProcessed')}:</strong>
                  </td>
                  <td>{summary.processed_count}</td>
                </tr>
                <tr>
                  <td>
                    <strong>{i18n.t('AccountLedger:totalCreated')}:</strong>
                  </td>
                  <td>{summary.created_count}</td>
                </tr>
                <tr>
                  <td>
                    <strong>{i18n.t('AccountLedger:totalUpdated')}:</strong>
                  </td>
                  <td>{summary.updated_count}</td>
                </tr>
                <tr>
                  <td>
                    <strong>{i18n.t('AccountLedger:invoicesLinked')}:</strong>
                  </td>
                  <td>{summary.invoice_linked_count}</td>
                </tr>
              </tbody>
            </Table>
          </CardBody>
        </Card>
      )}

      {/* Results Section */}
      {results.length > 0 && (
        <Card>
          <CardHeader>
            <h5>{i18n.t('AccountLedger:processingResults')}</h5>
          </CardHeader>
          <CardBody>
            <div style={{ overflowX: 'auto', maxHeight: '600px' }}>
              <Table striped bordered hover size="sm">
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr>
                    <th
                      colSpan={8}
                      className="bg-primary text-white"
                      style={{ position: 'sticky', top: 0, zIndex: 11 }}
                    >
                      {i18n.t('AccountLedger:resultStatus')}
                    </th>
                    <th
                      colSpan={10}
                      className="bg-secondary text-white"
                      style={{ position: 'sticky', top: 0, zIndex: 11 }}
                    >
                      {i18n.t('AccountLedger:originalData')}
                    </th>
                  </tr>
                  <tr>
                    <th style={{ position: 'sticky', top: '41px', zIndex: 11 }}>
                      {i18n.t('AccountLedger:id')}
                    </th>
                    <th style={{ position: 'sticky', top: '41px', zIndex: 11 }}>
                      {i18n.t('AccountLedger:created')}
                    </th>
                    <th style={{ position: 'sticky', top: '41px', zIndex: 11 }}>
                      {i18n.t('AccountLedger:updated')}
                    </th>
                    <th style={{ position: 'sticky', top: '41px', zIndex: 11 }}>
                      {i18n.t('AccountLedger:alreadyExisting')}
                    </th>
                    <th style={{ position: 'sticky', top: '41px', zIndex: 11 }}>
                      {i18n.t('AccountLedger:invoiceMatched')}
                    </th>
                    <th style={{ position: 'sticky', top: '41px', zIndex: 11 }}>
                      {i18n.t('AccountLedger:invoiceAlreadyLinked')}
                    </th>
                    <th style={{ position: 'sticky', top: '41px', zIndex: 11 }}>
                      {i18n.t('AccountLedger:store')}
                    </th>
                    <th style={{ position: 'sticky', top: '41px', zIndex: 11 }}>
                      {i18n.t('AccountLedger:matchLog')}
                    </th>
                    <th style={{ position: 'sticky', top: '41px', zIndex: 11 }}>
                      {i18n.t('AccountLedger:date')}
                    </th>
                    <th style={{ position: 'sticky', top: '41px', zIndex: 11 }}>
                      {i18n.t('AccountLedger:postingNumber')}
                    </th>
                    <th style={{ position: 'sticky', top: '41px', zIndex: 11 }}>
                      {i18n.t('AccountLedger:debitorAccountNumber')}
                    </th>
                    <th style={{ position: 'sticky', top: '41px', zIndex: 11 }}>
                      {i18n.t('AccountLedger:statementDescriptor')}
                    </th>
                    <th style={{ position: 'sticky', top: '41px', zIndex: 11 }}>
                      {i18n.t('AccountLedger:invoiceReference')}
                    </th>
                    <th style={{ position: 'sticky', top: '41px', zIndex: 11 }}>
                      {i18n.t('AccountLedger:debitAmount')}
                    </th>
                    <th style={{ position: 'sticky', top: '41px', zIndex: 11 }}>
                      {i18n.t('AccountLedger:creditAmount')}
                    </th>
                    <th style={{ position: 'sticky', top: '41px', zIndex: 11 }}>
                      {i18n.t('AccountLedger:batchNumber')}
                    </th>
                    <th style={{ position: 'sticky', top: '41px', zIndex: 11 }}>
                      {i18n.t('AccountLedger:costCenter1')}
                    </th>
                    <th style={{ position: 'sticky', top: '41px', zIndex: 11 }}>
                      {i18n.t('AccountLedger:costCenter2')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result, resultIndex) => {
                    // Map result index to original CSV row index
                    const originalIndex = processedIndices[resultIndex];
                    const csvRow = parsedData[originalIndex];
                    return (
                      <tr key={resultIndex}>
                        <td>{result.id}</td>
                        <td>{result.was_created ? '✓' : ''}</td>
                        <td>{result.was_updated ? '✓' : ''}</td>
                        <td>{result.was_already_existing ? '✓' : ''}</td>
                        <td>{result.billomat_invoice_matched ? '✓' : ''}</td>
                        <td>
                          {result.billomat_invoice_was_already_linked
                            ? '✓'
                            : ''}
                        </td>
                        <td>
                          {result.store ? (
                            <Link to={Routes.pickpackStores(result.store.id)}>
                              {result.store.name}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          <small>{result.match_log || '-'}</small>
                        </td>
                        {csvRow ? (
                          <>
                            <td>{csvRow.date}</td>
                            <td>{csvRow.postingNumber}</td>
                            <td>{csvRow.contraAccount}</td>
                            <td>{csvRow.postingText}</td>
                            <td>{csvRow.documentField1}</td>
                            <td>{csvRow.debitAmount}</td>
                            <td>{csvRow.creditAmount}</td>
                            <td>{csvRow.batchNumber}</td>
                            <td>{csvRow.costCenter1}</td>
                            <td>{csvRow.costCenter2}</td>
                          </>
                        ) : (
                          <>
                            <td colSpan={10}>-</td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Error Modal */}
      <Modal
        isOpen={errorModalOpen}
        toggle={() => setErrorModalOpen(!errorModalOpen)}
        size="lg"
      >
        <ModalHeader toggle={() => setErrorModalOpen(!errorModalOpen)}>
          {i18n.t('AccountLedger:errorModalTitle')}
        </ModalHeader>
        <ModalBody>
          <Alert color="danger">
            <h5>{error}</h5>
            <hr />
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {errorDetails}
            </pre>
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setErrorModalOpen(false)}>
            {i18n.t('Common:close')}
          </Button>
        </ModalFooter>
      </Modal>
    </ContentSection>
  );
}
